import { useState, useEffect } from "react"
import AdminSidebar from "../../components/admin/AdminSidebar"
import AdminHeader from "../../components/admin/AdminHeader"
import { useCategories } from "../../hooks/useCategories"
import { useCategoryTranslations } from "../../hooks/useCategoryTranslations"
import { useTranslation } from "../../i18n"
import { toastUtils } from "../../utils/toast"
import { 
  Search, 
  Plus, 
  Edit, 
  Trash2, 
  Eye,
  Globe,
  Tag,
  BookOpen,
  X,
  Palette,
  Smile
} from "lucide-react"
import { Button } from "../../components/ui/button"
import { Input } from "../../components/ui/input"
import { Label } from "../../components/ui/label"
import { Textarea } from "../../components/ui/textarea"

export default function CategoriesManagementPage() {
  const [searchTerm, setSearchTerm] = useState("")
  const [page, setPage] = useState(1)
  const [limit] = useState(10)
  const [categories, setCategories] = useState([])
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [selectedCategory, setSelectedCategory] = useState(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isViewModalOpen, setIsViewModalOpen] = useState(false)
  const [isTranslationModalOpen, setIsTranslationModalOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    icon: "",
    color: "#3B82F6"
  })
  const [formErrors, setFormErrors] = useState({})

  const { loading, getCategories, deleteCategory, updateCategory, createCategory } = useCategories()
  const { 
    loading: translationsLoading, 
    getTranslations, 
    createTranslation, 
    updateTranslation, 
    deleteTranslation 
  } = useCategoryTranslations()
  const { language } = useTranslation()

  // États pour la gestion des traductions
  const [translations, setTranslations] = useState([])
  const [selectedTranslation, setSelectedTranslation] = useState(null)
  const [translationFormData, setTranslationFormData] = useState({
    language: "",
    name: "",
    description: ""
  })
  const [translationFormErrors, setTranslationFormErrors] = useState({})
  const [isSubmittingTranslation, setIsSubmittingTranslation] = useState(false)
  const [showAddTranslationForm, setShowAddTranslationForm] = useState(false)

  // Langues supportées
  const SUPPORTED_LANGUAGES = [
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'es', name: 'Español', flag: '🇪🇸' },
    { code: 'ar', name: 'العربية', flag: '🇸🇦' }
  ]

  useEffect(() => {
    loadCategories()
  }, [page, searchTerm, language])

  const loadCategories = async () => {
    try {
      const filters = {
        page,
        limit,
        search: searchTerm || undefined,
        language: language || 'fr' // Utiliser l'API de traduction (l'API retourne automatiquement _count.courses)
      }
      
      const response = await getCategories(filters)
      
      if (response) {
        // La nouvelle API retourne { categories: [...], total, page, totalPages }
        if (Array.isArray(response)) {
          // Ancien format (tableau direct) - pour compatibilité
          setCategories(response)
          setTotal(response.length)
          setTotalPages(1)
        } else if (response.categories && Array.isArray(response.categories)) {
          // Nouveau format avec pagination
          setCategories(response.categories)
          setTotal(response.total || response.categories.length)
          setTotalPages(response.totalPages || 1)
        } else if (response.data && Array.isArray(response.data)) {
          // Format alternatif
          setCategories(response.data)
          setTotal(response.total || response.data.length)
          setTotalPages(response.totalPages || 1)
        } else {
          setCategories([])
          setTotal(0)
          setTotalPages(0)
        }
      }
    } catch (error) {
      toastUtils.error(error?.message || "Erreur lors du chargement des catégories")
    }
  }

  const handleDelete = async (category) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer la catégorie "${category.name}" ?`)) {
      return
    }

    try {
      await deleteCategory(category.id)
      toastUtils.success("Catégorie supprimée avec succès")
      loadCategories()
    } catch (error) {
      toastUtils.error(error?.message || "Erreur lors de la suppression")
    }
  }

  const handleView = async (category) => {
    try {
      // Charger les traductions
      const translations = await getTranslations(category.id)
      setSelectedCategory({ ...category, translations: translations?.data || [] })
      setIsViewModalOpen(true)
    } catch (error) {
      setSelectedCategory(category)
      setIsViewModalOpen(true)
    }
  }

  const handleEdit = (category) => {
    setSelectedCategory(category)
    setFormData({
      name: category.name || "",
      description: category.description || "",
      icon: category.icon || "",
      color: category.color || "#3B82F6"
    })
    setFormErrors({})
    setIsModalOpen(true)
  }

  const handleCreate = () => {
    setSelectedCategory(null)
    setFormData({
      name: "",
      description: "",
      icon: "",
      color: "#3B82F6"
    })
    setFormErrors({})
    setIsModalOpen(true)
  }

  const handleFormChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    // Effacer l'erreur pour ce champ
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: null
      }))
    }
  }

  const validateForm = () => {
    const errors = {}
    
    if (!formData.name.trim()) {
      errors.name = "Le nom est requis"
    }
    
    if (!formData.description.trim()) {
      errors.description = "La description est requise"
    } else if (formData.description.length < 10) {
      errors.description = "La description doit contenir au moins 10 caractères"
    }
    
    // Valider le format de la couleur hex
    if (formData.color && !/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(formData.color)) {
      errors.color = "Format de couleur invalide (ex: #3B82F6)"
    }
    
    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateForm()) {
      toastUtils.error("Veuillez corriger les erreurs du formulaire")
      return
    }
    
    setIsSubmitting(true)
    
    try {
      const categoryData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        icon: formData.icon.trim() || undefined,
        color: formData.color || undefined
      }
      
      if (selectedCategory) {
        // Modification
        await updateCategory(selectedCategory.id, categoryData)
        toastUtils.success("Catégorie modifiée avec succès")
      } else {
        // Création
        await createCategory(categoryData)
        toastUtils.success("Catégorie créée avec succès")
      }
      
      handleModalSuccess()
    } catch (error) {
      const errorMessage = Array.isArray(error?.response?.data?.message)
        ? error.response.data.message[0]
        : error?.response?.data?.message || error?.message || "Erreur lors de l'opération"
      toastUtils.error(errorMessage)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleManageTranslations = async (category) => {
    setSelectedCategory(category)
    setIsTranslationModalOpen(true)
    setShowAddTranslationForm(false)
    setSelectedTranslation(null)
    await loadCategoryTranslations(category.id)
  }

  const loadCategoryTranslations = async (categoryId) => {
    try {
      const response = await getTranslations(categoryId)
      // L'API retourne un tableau de traductions
      const translationsList = Array.isArray(response) 
        ? response 
        : response?.data || []
      setTranslations(translationsList)
    } catch (error) {
      console.error('Error loading translations:', error)
      setTranslations([])
    }
  }

  const handleAddTranslation = () => {
    setSelectedTranslation(null)
    setTranslationFormData({
      language: "",
      name: "",
      description: ""
    })
    setTranslationFormErrors({})
    setShowAddTranslationForm(true)
  }

  const handleEditTranslation = (translation) => {
    setSelectedTranslation(translation)
    setTranslationFormData({
      language: translation.language,
      name: translation.name || "",
      description: translation.description || ""
    })
    setTranslationFormErrors({})
    setShowAddTranslationForm(true)
  }

  const handleDeleteTranslation = async (translation) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer la traduction en ${translation.language} ?`)) {
      return
    }

    try {
      await deleteTranslation(selectedCategory.id, translation.language)
      toastUtils.success("Traduction supprimée avec succès")
      await loadCategoryTranslations(selectedCategory.id)
    } catch (error) {
      const errorMessage = Array.isArray(error?.response?.data?.message)
        ? error.response.data.message[0]
        : error?.response?.data?.message || error?.message || "Erreur lors de la suppression"
      toastUtils.error(errorMessage)
    }
  }

  const validateTranslationForm = () => {
    const errors = {}
    
    if (!translationFormData.language) {
      errors.language = "La langue est requise"
    }
    
    if (!translationFormData.name.trim()) {
      errors.name = "Le nom est requis"
    }
    
    if (!translationFormData.description.trim()) {
      errors.description = "La description est requise"
    } else if (translationFormData.description.length < 10) {
      errors.description = "La description doit contenir au moins 10 caractères"
    }
    
    setTranslationFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmitTranslation = async (e) => {
    e.preventDefault()
    
    if (!validateTranslationForm()) {
      toastUtils.error("Veuillez corriger les erreurs du formulaire")
      return
    }
    
    setIsSubmittingTranslation(true)
    
    try {
      const translationData = {
        language: translationFormData.language,
        name: translationFormData.name.trim(),
        description: translationFormData.description.trim()
      }
      
      if (selectedTranslation) {
        // Mise à jour
        await updateTranslation(selectedCategory.id, selectedTranslation.language, {
          name: translationData.name,
          description: translationData.description
        })
        toastUtils.success("Traduction modifiée avec succès")
      } else {
        // Création
        await createTranslation(selectedCategory.id, translationData)
        toastUtils.success("Traduction créée avec succès")
      }
      
      // Recharger les traductions
      await loadCategoryTranslations(selectedCategory.id)
      setShowAddTranslationForm(false)
      setSelectedTranslation(null)
      setTranslationFormData({
        language: "",
        name: "",
        description: ""
      })
    } catch (error) {
      const errorMessage = Array.isArray(error?.response?.data?.message)
        ? error.response.data.message[0]
        : error?.response?.data?.message || error?.message || "Erreur lors de l'opération"
      toastUtils.error(errorMessage)
    } finally {
      setIsSubmittingTranslation(false)
    }
  }

  const getAvailableLanguages = () => {
    const existingLanguages = translations.map(t => t.language)
    return SUPPORTED_LANGUAGES.filter(lang => !existingLanguages.includes(lang.code))
  }

  const handleModalSuccess = () => {
    setIsModalOpen(false)
    setSelectedCategory(null)
    loadCategories()
  }

  return (
    <div className="flex h-screen bg-gray-50">
      <AdminSidebar />
      <div className="flex-1 ml-64 p-8 overflow-y-auto">
        <AdminHeader 
          title="Gestion des Catégories" 
          subtitle="Créer, modifier et gérer les catégories de cours"
        />

        {/* Actions Bar */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6 flex items-center justify-between">
          <div className="flex items-center gap-4 flex-1">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Rechercher une catégorie..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value)
                  setPage(1)
                }}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#141835] focus:border-transparent"
              />
            </div>
          </div>

          <Button onClick={handleCreate} className="bg-[#141835] hover:bg-[#203a8a]">
            <Plus size={20} className="mr-2" />
            Créer une catégorie
          </Button>
        </div>

        {/* Categories Table */}
        {loading ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#141835] mx-auto mb-4"></div>
            <p className="text-gray-600">Chargement des catégories...</p>
          </div>
        ) : categories.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <Tag className="mx-auto mb-4 text-gray-400" size={48} />
            <p className="text-gray-600">Aucune catégorie trouvée</p>
          </div>
        ) : (
          <>
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nom</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Icône</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Couleur</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nombre de cours</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {categories.map((category) => (
                    <tr key={category.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <Tag size={16} className="mr-2 text-[#141835]" />
                          <span className="font-medium text-gray-900">{category.name}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 max-w-xs truncate">
                          {category.description || "Aucune description"}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-2xl">{category.icon || "📁"}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {category.color && (
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-6 h-6 rounded border border-gray-300"
                              style={{ backgroundColor: category.color }}
                            />
                            <span className="text-sm text-gray-500">{category.color}</span>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <BookOpen size={14} className="mr-1 text-gray-400" />
                          <span className="text-sm text-gray-900">
                            {category._count?.courses || 0}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleView(category)}
                            className="text-[#141835] hover:text-[#203a8a]"
                            title="Voir les détails"
                          >
                            <Eye size={18} />
                          </button>
                          <button
                            onClick={() => handleManageTranslations(category)}
                            className="text-purple-600 hover:text-purple-800"
                            title="Gérer les traductions"
                          >
                            <Globe size={18} />
                          </button>
                          <button
                            onClick={() => handleEdit(category)}
                            className="text-blue-600 hover:text-blue-800"
                            title="Modifier"
                          >
                            <Edit size={18} />
                          </button>
                          <button
                            onClick={() => handleDelete(category)}
                            className="text-red-600 hover:text-red-800"
                            title="Supprimer"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-6 flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Page {page} sur {totalPages} ({total} catégories)
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    variant="outline"
                  >
                    Précédent
                  </Button>
                  <Button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    variant="outline"
                  >
                    Suivant
                  </Button>
                </div>
              </div>
            )}
          </>
        )}

        {/* Create/Edit Modal */}
        {isModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setIsModalOpen(false)}>
            <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">
                  {selectedCategory ? "Modifier la catégorie" : "Créer une catégorie"}
                </h2>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Nom */}
                <div>
                  <Label htmlFor="name" className="text-sm font-medium text-gray-700 mb-2 block">
                    Nom <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleFormChange}
                    placeholder="Ex: Développement Web"
                    required
                    className={formErrors.name ? "border-red-500" : ""}
                  />
                  {formErrors.name && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.name}</p>
                  )}
                </div>

                {/* Description */}
                <div>
                  <Label htmlFor="description" className="text-sm font-medium text-gray-700 mb-2 block">
                    Description <span className="text-red-500">*</span>
                  </Label>
                  <Textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleFormChange}
                    placeholder="Ex: Cours de développement web frontend et backend"
                    required
                    rows={4}
                    className={formErrors.description ? "border-red-500" : ""}
                  />
                  {formErrors.description && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.description}</p>
                  )}
                </div>

                {/* Icône et Couleur */}
                <div className="grid grid-cols-2 gap-4">
                  {/* Icône */}
                  <div>
                    <Label htmlFor="icon" className="text-sm font-medium text-gray-700 mb-2 block flex items-center gap-2">
                      <Smile size={16} />
                      Icône (emoji)
                    </Label>
                    <div className="flex items-center gap-2">
                      <Input
                        id="icon"
                        name="icon"
                        value={formData.icon}
                        onChange={handleFormChange}
                        placeholder="🌐"
                        maxLength={2}
                        className="w-20"
                      />
                      {formData.icon && (
                        <div className="text-2xl bg-gray-100 rounded p-2">
                          {formData.icon}
                        </div>
                      )}
                    </div>
                    <p className="mt-1 text-xs text-gray-500">Un emoji pour représenter la catégorie</p>
                  </div>

                  {/* Couleur */}
                  <div>
                    <Label htmlFor="color" className="text-sm font-medium text-gray-700 mb-2 block flex items-center gap-2">
                      <Palette size={16} />
                      Couleur
                    </Label>
                    <div className="flex items-center gap-2">
                      <Input
                        id="color"
                        name="color"
                        type="color"
                        value={formData.color}
                        onChange={handleFormChange}
                        className="w-20 h-10 cursor-pointer"
                      />
                      <Input
                        type="text"
                        value={formData.color}
                        onChange={handleFormChange}
                        name="color"
                        placeholder="#3B82F6"
                        pattern="^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$"
                        className={formErrors.color ? "border-red-500 flex-1" : "flex-1"}
                      />
                    </div>
                    {formErrors.color && (
                      <p className="mt-1 text-sm text-red-600">{formErrors.color}</p>
                    )}
                    <p className="mt-1 text-xs text-gray-500">Code couleur hexadécimal</p>
                  </div>
                </div>

                {/* Aperçu */}
                {(formData.name || formData.icon || formData.color) && (
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <Label className="text-sm font-medium text-gray-700 mb-2 block">Aperçu</Label>
                    <div className="flex items-center gap-3 p-3 bg-white rounded border border-gray-200">
                      {formData.icon && (
                        <span className="text-2xl">{formData.icon}</span>
                      )}
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">
                          {formData.name || "Nom de la catégorie"}
                        </div>
                        <div className="text-sm text-gray-600">
                          {formData.description || "Description de la catégorie"}
                        </div>
                      </div>
                      {formData.color && (
                        <div
                          className="w-8 h-8 rounded border border-gray-300"
                          style={{ backgroundColor: formData.color }}
                        />
                      )}
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsModalOpen(false)}
                    disabled={isSubmitting}
                  >
                    Annuler
                  </Button>
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="bg-[#141835] hover:bg-[#203a8a]"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        {selectedCategory ? "Modification..." : "Création..."}
                      </>
                    ) : (
                      selectedCategory ? "Modifier" : "Créer"
                    )}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* View Modal - TODO: Créer un composant modal dédié */}
        {isViewModalOpen && selectedCategory && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <h2 className="text-2xl font-bold mb-4">Détails de la catégorie</h2>
              <div className="space-y-4">
                <div>
                  <label className="font-medium">Nom:</label>
                  <p>{selectedCategory.name}</p>
                </div>
                <div>
                  <label className="font-medium">Description:</label>
                  <p>{selectedCategory.description || "Aucune description"}</p>
                </div>
                <div>
                  <label className="font-medium">Icône:</label>
                  <p className="text-2xl">{selectedCategory.icon || "📁"}</p>
                </div>
                <div>
                  <label className="font-medium">Couleur:</label>
                  {selectedCategory.color && (
                    <div className="flex items-center gap-2 mt-1">
                      <div 
                        className="w-8 h-8 rounded border border-gray-300"
                        style={{ backgroundColor: selectedCategory.color }}
                      />
                      <span>{selectedCategory.color}</span>
                    </div>
                  )}
                </div>
                <div>
                  <label className="font-medium">Nombre de cours:</label>
                  <p>{selectedCategory._count?.courses || 0}</p>
                </div>
                {selectedCategory.translations && selectedCategory.translations.length > 0 && (
                  <div>
                    <label className="font-medium">Traductions:</label>
                    <div className="mt-2 space-y-2">
                      {selectedCategory.translations.map((translation) => (
                        <div key={translation.id} className="p-2 bg-gray-50 rounded">
                          <span className="font-medium">{translation.language}:</span> {translation.name}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div className="flex justify-end mt-6">
                <Button onClick={() => setIsViewModalOpen(false)}>
                  Fermer
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Translation Management Modal */}
        {isTranslationModalOpen && selectedCategory && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setIsTranslationModalOpen(false)}>
            <div className="bg-white rounded-lg p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Gérer les traductions</h2>
                  <p className="text-gray-600 mt-1">Catégorie: {selectedCategory.name}</p>
                </div>
                <button
                  onClick={() => setIsTranslationModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X size={24} />
                </button>
              </div>

              {translationsLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#141835] mx-auto mb-4"></div>
                  <p className="text-gray-600">Chargement des traductions...</p>
                </div>
              ) : (
                <>
                  {/* Liste des traductions existantes */}
                  {!showAddTranslationForm && (
                    <>
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-gray-900">Traductions existantes</h3>
                        {getAvailableLanguages().length > 0 && (
                          <Button
                            onClick={handleAddTranslation}
                            className="bg-[#141835] hover:bg-[#203a8a]"
                          >
                            <Plus size={16} className="mr-2" />
                            Ajouter une traduction
                          </Button>
                        )}
                      </div>

                      {translations.length === 0 ? (
                        <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
                          <Globe className="mx-auto mb-4 text-gray-400" size={48} />
                          <p className="text-gray-600 mb-2">Aucune traduction disponible</p>
                          {getAvailableLanguages().length > 0 && (
                            <Button
                              onClick={handleAddTranslation}
                              variant="outline"
                              className="mt-4"
                            >
                              <Plus size={16} className="mr-2" />
                              Ajouter la première traduction
                            </Button>
                          )}
                        </div>
                      ) : (
                        <div className="space-y-3 mb-6">
                          {translations.map((translation) => {
                            const langInfo = SUPPORTED_LANGUAGES.find(l => l.code === translation.language)
                            return (
                              <div
                                key={translation.id}
                                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors"
                              >
                                <div className="flex items-center gap-3 flex-1">
                                  <span className="text-2xl">{langInfo?.flag || '🌐'}</span>
                                  <div className="flex-1">
                                    <div className="font-medium text-gray-900">
                                      {langInfo?.name || translation.language} ({translation.language})
                                    </div>
                                    <div className="text-sm text-gray-600 mt-1">
                                      <div><strong>Nom:</strong> {translation.name}</div>
                                      <div className="truncate max-w-md">
                                        <strong>Description:</strong> {translation.description}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleEditTranslation(translation)}
                                  >
                                    <Edit size={14} className="mr-1" />
                                    Modifier
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleDeleteTranslation(translation)}
                                    className="text-red-600 hover:text-red-700 hover:border-red-300"
                                  >
                                    <Trash2 size={14} />
                                  </Button>
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      )}

                      {getAvailableLanguages().length > 0 && translations.length > 0 && (
                        <div className="mt-4">
                          <Button
                            onClick={handleAddTranslation}
                            variant="outline"
                            className="w-full"
                          >
                            <Plus size={16} className="mr-2" />
                            Ajouter une autre traduction
                          </Button>
                        </div>
                      )}
                    </>
                  )}

                  {/* Formulaire d'ajout/modification */}
                  {showAddTranslationForm && (
                    <form onSubmit={handleSubmitTranslation} className="space-y-4">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {selectedTranslation ? "Modifier la traduction" : "Ajouter une traduction"}
                        </h3>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setShowAddTranslationForm(false)
                            setSelectedTranslation(null)
                            setTranslationFormData({ language: "", name: "", description: "" })
                            setTranslationFormErrors({})
                          }}
                        >
                          <X size={16} />
                        </Button>
                      </div>

                      {/* Sélection de la langue */}
                      <div>
                        <Label htmlFor="translation-language" className="text-sm font-medium text-gray-700 mb-2 block">
                          Langue <span className="text-red-500">*</span>
                        </Label>
                        {selectedTranslation ? (
                          <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                            <span className="text-xl">
                              {SUPPORTED_LANGUAGES.find(l => l.code === selectedTranslation.language)?.flag || '🌐'}
                            </span>
                            <span className="font-medium">
                              {SUPPORTED_LANGUAGES.find(l => l.code === selectedTranslation.language)?.name || selectedTranslation.language}
                            </span>
                            <span className="text-sm text-gray-500">({selectedTranslation.language})</span>
                          </div>
                        ) : (
                          <select
                            id="translation-language"
                            name="language"
                            value={translationFormData.language}
                            onChange={(e) => {
                              setTranslationFormData(prev => ({ ...prev, language: e.target.value }))
                              if (translationFormErrors.language) {
                                setTranslationFormErrors(prev => ({ ...prev, language: null }))
                              }
                            }}
                            required
                            className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835] ${
                              translationFormErrors.language ? "border-red-500" : "border-gray-300"
                            }`}
                          >
                            <option value="">Sélectionnez une langue</option>
                            {getAvailableLanguages().map(lang => (
                              <option key={lang.code} value={lang.code}>
                                {lang.flag} {lang.name} ({lang.code})
                              </option>
                            ))}
                          </select>
                        )}
                        {translationFormErrors.language && (
                          <p className="mt-1 text-sm text-red-600">{translationFormErrors.language}</p>
                        )}
                      </div>

                      {/* Nom */}
                      <div>
                        <Label htmlFor="translation-name" className="text-sm font-medium text-gray-700 mb-2 block">
                          Nom <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="translation-name"
                          name="name"
                          value={translationFormData.name}
                          onChange={(e) => {
                            setTranslationFormData(prev => ({ ...prev, name: e.target.value }))
                            if (translationFormErrors.name) {
                              setTranslationFormErrors(prev => ({ ...prev, name: null }))
                            }
                          }}
                          placeholder="Ex: Web Development"
                          required
                          className={translationFormErrors.name ? "border-red-500" : ""}
                        />
                        {translationFormErrors.name && (
                          <p className="mt-1 text-sm text-red-600">{translationFormErrors.name}</p>
                        )}
                      </div>

                      {/* Description */}
                      <div>
                        <Label htmlFor="translation-description" className="text-sm font-medium text-gray-700 mb-2 block">
                          Description <span className="text-red-500">*</span>
                        </Label>
                        <Textarea
                          id="translation-description"
                          name="description"
                          value={translationFormData.description}
                          onChange={(e) => {
                            setTranslationFormData(prev => ({ ...prev, description: e.target.value }))
                            if (translationFormErrors.description) {
                              setTranslationFormErrors(prev => ({ ...prev, description: null }))
                            }
                          }}
                          placeholder="Ex: Frontend and backend web development courses"
                          required
                          rows={4}
                          className={translationFormErrors.description ? "border-red-500" : ""}
                        />
                        {translationFormErrors.description && (
                          <p className="mt-1 text-sm text-red-600">{translationFormErrors.description}</p>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex justify-end gap-3 pt-4 border-t">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            setShowAddTranslationForm(false)
                            setSelectedTranslation(null)
                            setTranslationFormData({ language: "", name: "", description: "" })
                            setTranslationFormErrors({})
                          }}
                          disabled={isSubmittingTranslation}
                        >
                          Annuler
                        </Button>
                        <Button
                          type="submit"
                          disabled={isSubmittingTranslation}
                          className="bg-[#141835] hover:bg-[#203a8a]"
                        >
                          {isSubmittingTranslation ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                              {selectedTranslation ? "Modification..." : "Création..."}
                            </>
                          ) : (
                            selectedTranslation ? "Modifier" : "Créer"
                          )}
                        </Button>
                      </div>
                    </form>
                  )}

                  {/* Bouton fermer */}
                  {!showAddTranslationForm && (
                    <div className="flex justify-end mt-6 pt-4 border-t">
                      <Button variant="outline" onClick={() => setIsTranslationModalOpen(false)}>
                        Fermer
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

